import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { CommonService } from 'src/app/services/Common-function/common.service';

export interface DateRangeSelection {
  startDate: string; // YYYY-MM-DD for API params
  endDate: string;   // YYYY-MM-DD for API params
  label: string;     // human readable label shown on the trigger
  key: string;       // preset key, or 'custom' for a hand-picked range
}

interface Preset {
  key: string;
  label: string;
}

// A Material date-range picker with a preset shortcut list beside the calendar.
@Component({
  selector: 'ipm-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit {
  // Preset to select on first render (defaults to "Last 30 days").
  @Input() initialKey = 'last_30_days';
  @Output() rangeChange = new EventEmitter<DateRangeSelection>();

  @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<unknown>;

  // Whether the dialog/bottom-sheet is open (drives the trigger chevron).
  isOpen = false;

  readonly presets: Preset[] = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'today_yesterday', label: 'Today & Yesterday' },
    { key: 'last_7_days', label: 'Last 7 days' },
    { key: 'last_14_days', label: 'Last 14 days' },
    { key: 'last_28_days', label: 'Last 28 days' },
    { key: 'last_30_days', label: 'Last 30 days' },
    { key: 'this_week', label: 'This week' },
    { key: 'last_week', label: 'Last week' },
    { key: 'this_month', label: 'This month' }
  ];

  // Don't allow picking future dates.
  readonly maxDate = new Date();

  // Committed selection (reflected on the trigger).
  selectedKey = 'last_30_days';
  selectedRange = new DateRange<Date>(null, null);
  triggerLabel = '';

  // Draft selection while the panel is open (committed on Apply).
  draftKey = '';
  draftRange = new DateRange<Date>(null, null);

  constructor(private commonService: CommonService) {}

  ngOnInit(): void {
    this.selectedKey = this.initialKey;
    const { start, end } = this.computeRange(this.initialKey);
    this.selectedRange = new DateRange(start, end);
    this.updateTriggerLabel();
  }

  // Open the picker via the shared modal helper — a dialog on desktop and a
  // bottom sheet on mobile, both with the app's standard styling. The draft
  // resets to the committed selection each time it opens.
  openDialog(): void {
    if (this.isOpen) {
      return;
    }
    this.draftKey = this.selectedKey;
    this.draftRange = this.selectedRange;
    this.isOpen = true;
    this.commonService.openModel(this.dialogTemplate, {}, 'ipm-daterange-dialog');
    // Reset the chevron state whichever way the popup is dismissed.
    const closed$ = this.commonService.isMobileDisableClick
      ? this.commonService.bottomRef.afterDismissed()
      : this.commonService.modelRef.afterClosed();
    closed$.subscribe(() => {
      this.isOpen = false;
    });
  }

  closeDialog(): void {
    this.commonService.closePopUp();
  }

  selectPreset(preset: Preset): void {
    this.draftKey = preset.key;
    const { start, end } = this.computeRange(preset.key);
    this.draftRange = new DateRange(start, end);
  }

  // Mobile <select> hands back just the key.
  selectPresetByKey(key: string): void {
    const preset = this.presets.find((p) => p.key === key);
    if (preset) {
      this.selectPreset(preset);
    }
  }

  // mat-calendar emits one date at a time; build the range from successive clicks.
  onCalendarChange(date: Date | null): void {
    if (!date) {
      return;
    }
    const start = this.draftRange.start;
    const end = this.draftRange.end;
    if (start && !end && date >= start) {
      this.draftRange = new DateRange(start, date);
    } else {
      this.draftRange = new DateRange(date, null);
    }
    this.draftKey = 'custom';
  }

  apply(): void {
    if (!this.draftRange.start || !this.draftRange.end) {
      return;
    }
    this.selectedKey = this.draftKey;
    this.selectedRange = this.draftRange;
    this.updateTriggerLabel();
    this.rangeChange.emit({
      startDate: this.toApiDate(this.draftRange.start),
      endDate: this.toApiDate(this.draftRange.end),
      label: this.triggerLabel,
      key: this.selectedKey
    });
    this.closeDialog();
  }

  cancel(): void {
    this.draftKey = this.selectedKey;
    this.draftRange = this.selectedRange;
    this.closeDialog();
  }

  // Readable summary of the in-progress draft shown in the panel footer.
  get draftReadout(): string {
    if (this.draftRange.start && this.draftRange.end) {
      return `${this.formatLong(this.draftRange.start)} – ${this.formatLong(this.draftRange.end)}`;
    }
    if (this.draftRange.start) {
      return `From ${this.formatLong(this.draftRange.start)}`;
    }
    return 'Select a date range';
  }

  private updateTriggerLabel(): void {
    const preset = this.presets.find((p) => p.key === this.selectedKey);
    if (preset) {
      this.triggerLabel = preset.label;
      return;
    }
    if (this.selectedRange.start && this.selectedRange.end) {
      this.triggerLabel = `${this.formatShort(this.selectedRange.start)} – ${this.formatShort(this.selectedRange.end)}`;
      return;
    }
    this.triggerLabel = 'Select range';
  }

  // Maps a preset key to a concrete date range. "Last N days" windows count back
  // N days from today; the week/month presets snap to calendar boundaries.
  private computeRange(key: string): { start: Date; end: Date } {
    const today = this.startOfDay(new Date());
    switch (key) {
      case 'today':
        return { start: today, end: today };
      case 'yesterday': {
        const yesterday = this.addDays(today, -1);
        return { start: yesterday, end: yesterday };
      }
      case 'today_yesterday':
        return { start: this.addDays(today, -1), end: today };
      case 'last_7_days':
        return { start: this.addDays(today, -7), end: today };
      case 'last_14_days':
        return { start: this.addDays(today, -14), end: today };
      case 'last_28_days':
        return { start: this.addDays(today, -28), end: today };
      case 'this_week':
        return { start: this.startOfWeek(today), end: today };
      case 'last_week': {
        const lastWeekStart = this.addDays(this.startOfWeek(today), -7);
        return { start: lastWeekStart, end: this.addDays(lastWeekStart, 6) };
      }
      case 'this_month':
        return { start: this.startOfMonth(today), end: today };
      case 'last_30_days':
      default:
        return { start: this.addDays(today, -30), end: today };
    }
  }

  private startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
  }

  // Monday as the first day of the week (en-GB convention).
  private startOfWeek(date: Date): Date {
    const next = this.startOfDay(date);
    const daysSinceMonday = (next.getDay() + 6) % 7;
    return this.addDays(next, -daysSinceMonday);
  }

  private startOfMonth(date: Date): Date {
    return this.startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private toApiDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatLong(date: Date): string {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private formatShort(date: Date): string {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }
}
